import { Check, Clock3, FileText, Mail, Send, Sparkles } from 'lucide-react';
import type { ComponentType, ReactElement } from 'react';

import { cn } from '@/shared/lib/utils';

/**
 * 서비스 슬라이드 오른쪽 패널의 그림 — 수업·납품에서 실제로 보게 될 화면을 한 장면으로 보여준다.
 * 전부 장식이다(패널이 aria-hidden). 내용은 왼쪽 문장이 말한다.
 */

const PANEL = 'relative aspect-[5/4] w-full overflow-hidden rounded-[36px]';

/** 창 하나 — 머리띠(도구 이름·점 셋)와 본문 줄 */
function ToolWindow({ tool, className }: { tool: string; className: string }): ReactElement {
  return (
    <div
      className={cn(
        'absolute flex flex-col gap-3 rounded-2xl bg-background p-4 shadow-[0_20px_40px_-24px_rgb(6_17_11/0.5)]',
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <span className="rounded-full bg-mint-soft px-2.5 py-0.5 text-[11px] font-semibold text-on-mint">
          {tool}
        </span>
        <span className="flex gap-1">
          {['a', 'b', 'c'].map((dot) => (
            <span key={dot} className="size-1.5 rounded-full bg-foreground/15" />
          ))}
        </span>
      </div>
      {[88, 72, 80, 56].map((width) => (
        <span
          key={width}
          className="h-2 rounded-full bg-foreground/10"
          style={{ width: `${width}%` }}
        />
      ))}
    </div>
  );
}

/** 01 AI 교육 — 도구별 대화창이 겹겹이 쌓이고, 맨 앞 창에서 AI 가 답을 쓰는 중이다 */
export function LessonStackArt(): ReactElement {
  return (
    <div aria-hidden="true" className={cn(PANEL, 'bg-primary')}>
      <ToolWindow tool="Gemini" className="top-[9%] right-[7%] h-[46%] w-[52%] rotate-[4deg]" />
      <ToolWindow tool="Claude" className="top-[19%] right-[17%] h-[46%] w-[52%] rotate-[1deg]" />

      <div className="-rotate-2 absolute bottom-[9%] left-[9%] flex h-[56%] w-[62%] flex-col gap-3 rounded-2xl bg-ink-surface p-5 text-on-ink shadow-[0_30px_50px_-24px_rgb(6_17_11/0.6)]">
        <div className="flex items-center justify-between">
          <span className="rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-semibold text-on-mint">
            ChatGPT
          </span>
          <span className="flex gap-1">
            {['a', 'b', 'c'].map((dot) => (
              <span key={dot} className="size-1.5 rounded-full bg-on-ink/25" />
            ))}
          </span>
        </div>
        <span className="self-end rounded-2xl rounded-br-sm bg-background px-3 py-1.5 text-[12px] font-medium text-foreground">
          이번 주 회의록 요약해 줘
        </span>
        <div className="flex gap-2.5">
          <Sparkles className="size-4 shrink-0 text-primary" strokeWidth={2} />
          <div className="flex flex-1 flex-col gap-2 pt-1">
            <span className="h-2 w-[86%] rounded-full bg-primary" />
            <span className="h-2 w-[94%] rounded-full bg-on-ink/45" />
            <span className="flex items-center gap-1">
              <span className="h-2 w-[58%] rounded-full bg-on-ink/30" />
              <span className="h-3 w-0.5 rounded-full bg-primary motion-safe:animate-pulse" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

type FlowStep = {
  readonly label: string;
  readonly Icon: ComponentType<{ className?: string; strokeWidth?: number }>;
};

const FLOW_STEPS: readonly FlowStep[] = [
  { label: '메일·메신저 수집', Icon: Mail },
  { label: 'AI가 요약·분류', Icon: Sparkles },
  { label: '보고서 초안 작성', Icon: FileText },
];

/** 02 자동화 컨설팅 — 사람 손을 거치지 않고 매주 같은 시각에 끝까지 흘러가는 업무 */
export function AutomationFlowArt(): ReactElement {
  return (
    <div aria-hidden="true" className={cn(PANEL, 'bg-ink-surface text-on-ink')}>
      <span className="-top-12 -right-16 absolute h-44 w-72 bg-primary/12 [transform:skewX(var(--slant))]" />
      <div className="absolute inset-0 flex flex-col justify-center gap-3 px-[12%]">
        <span className="mb-2 flex w-fit items-center gap-2 rounded-full border border-on-ink/20 px-3 py-1.5 font-mono text-[11.5px] text-on-ink/80">
          <Clock3 className="size-3.5 text-primary" strokeWidth={2} />
          매주 월 09:00 · 자동 실행
        </span>

        {/* 단계 사이를 잇는 세로선 — 흐름이 한 줄로 이어진다는 것을 보여준다 */}
        <div className="relative flex flex-col gap-3">
          <span className="absolute top-4 bottom-4 left-[1.375rem] w-px bg-primary/40" />
          {FLOW_STEPS.map(({ label, Icon }, index) => (
            <span
              key={label}
              className="relative flex items-center gap-3 rounded-2xl border border-on-ink/12 bg-on-ink/6 px-3 py-2.5 text-[13.5px] font-medium"
              style={{ marginLeft: `${index * 6}%` }}
            >
              <span className="grid size-7 place-items-center rounded-full bg-on-ink/10">
                <Icon className="size-3.5 text-primary" strokeWidth={2} />
              </span>
              {label}
            </span>
          ))}
          <span className="relative ml-[18%] flex items-center gap-3 rounded-2xl bg-primary px-3 py-2.5 text-[13.5px] font-semibold text-on-mint">
            <span className="grid size-7 place-items-center rounded-full bg-on-mint text-primary">
              <Send className="size-3.5" strokeWidth={2.2} />
            </span>
            팀 채널에 공유
            <Check className="ml-auto size-4" strokeWidth={2.6} />
          </span>
        </div>
      </div>
    </div>
  );
}
